'use client';

import ease from '@/constants/ease';
import useStore from '@/stores/useStore';
import { motion } from 'framer-motion';
import React, { FC, useState } from 'react';
import IconTileBreakup from '../IconTileBreakup';

export interface ComparePlansProps {
  comparePlansFeatures: {
    title: React.ReactNode;
    isOpen: boolean;
    features: {
      topMargin: boolean;
      largeTooltip: boolean;
      toolTip: React.ReactNode;
      columnOne: React.ReactNode;
      columnTwo: React.ReactNode;
      columnThree: React.ReactNode;
      columnFour: React.ReactNode;
    }[]
  }[];
}

const ComparePlans: FC<ComparePlansProps> = ({
  comparePlansFeatures = []
}) => {
  const [activeFeatureIndex, setFeatureIndex] = useState([0, 1, 2]);
  const [toolTip, setToolTip] = useState('place-holder');

  const setActiveToolTip = (column) => {
    setToolTip(column);
  }

  const addItem = (newItem) => {
    if (!activeFeatureIndex.includes(newItem)) {
      setFeatureIndex([...activeFeatureIndex, newItem]);
    } else {
      setFeatureIndex(prevItems => prevItems.filter(item => item !== newItem));
    }
  };

  const { setDemoModalOpen, setTrialModalOpen } = useStore();

  const onClickMenuButton = (action) => {
    switch (action) {
      case 'trial':
        setTrialModalOpen(true);
        break;
      case 'demo':
        setDemoModalOpen(true);
        break;

      default:
        break;
    }
  };

  return (
    <>
      <section className="flex md:pt-[64px] md:pb-[180px] px-[24px] bg-white md:bg-gray-100 overflow-scroll md:overflow-visible">
        <div className="w-full mx-auto max-w-grid grid grid-cols-[repeat(4,minmax(150px,1fr))] md:grid-cols-4 gap-x-2 rounded-12">

          <div className="col-span-full grid w-full mx-auto max-w-grid grid-cols-4 bg-white md:bg-gray-100 py-[16px] sticky top-[80px] z-[100] items-center">
            <div className="flex w-full w-[160px] md:w-[250px]">
              <h2 className="text-blue-900 font-500 text-p1 md:text-h1">Compare plans</h2>
            </div>
            <div className="flex flex-wrap w-[160px] md:w-[250px] justify-center">
              <div className="flex w-full justify-center">
                <h3 className="font-500 text-blue-900 text-p1">Starter</h3>
              </div>
              <div className="flex w-full justify-center mt-[12px]">
                <button
                  className="block button button-size-medium button-secondary-violet"
                  onClick={() => {
                    onClickMenuButton("trial");
                  }}
                >
                  Try for Free
                </button>
              </div>
            </div>
            <div className="flex flex-wrap w-[160px] md:w-[250px] justify-center">
              <div className="flex w-full justify-center">
                <h3 className="font-500 text-blue-900 text-p1">Professional</h3>
              </div>
              <div className="flex w-full justify-center mt-[12px]">
                <button
                  className="block button button-size-medium button-secondary-violet"
                  onClick={() => {
                    onClickMenuButton("trial");
                  }}
                >
                  Try for Free
                </button>
              </div>
            </div>
            <div className="flex flex-wrap w-[160px] md:w-[250px]">
              <div className="flex w-full justify-center">
                <h3 className="font-500 text-blue-900 text-p1">Enterprise</h3>
              </div>
              <div className="flex w-full justify-center mt-[12px]">
                <button
                  className="block button button-size-medium button-primary-violet"
                  onClick={() => {
                    onClickMenuButton("demo");
                  }}
                >
                  Request a Demo
                </button>
              </div>
            </div>
          </div>

          {comparePlansFeatures.map((item, index) => (
            <>
              <div key={`compare-plans-features-${index}`} className="flex col-span-full w-full items-center justify-between cursor-pointer sticky bg-white md:bg-gray-100 z-[100] top-[180px]" onClick={() => { addItem(index) }}>
                <div className={`flex text-p1 font-500 mt-[24px] border-b-[1px] border-solid ${activeFeatureIndex.includes(index) ? "border-gray-500" : "border-gray-300"} w-full items-center justify-between pb-[12px]`}>
                  <div className="flex">
                    {item.title}
                  </div>

                  <div className="flex">
                    {activeFeatureIndex.includes(index) ? <motion.div
                      className="text-p2 mt-[10px] font-400 text-blue-900 w-full"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 400 / 1000,
                        ease: ease.appear,
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 15L12 9L6 15" stroke="#0D1D39" strokeWidth="2.57143" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </motion.div> : <motion.div
                      className="text-p2 mt-[10px] font-400 text-blue-900 w-full"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 400 / 1000,
                        ease: ease.appear,
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 9L12 15L6 9" stroke="#0D1D39" strokeWidth="2.57143" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </motion.div>}
                  </div>
                </div>
              </div>
              <div className="col-span-full grid">

                {activeFeatureIndex.includes(index) && (
                  <motion.div
                    className="grid grid-cols-4"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 400 / 1000,
                      ease: ease.appear,
                    }}
                  >
                    {item.features.map((feature, index) => (
                      <React.Fragment key={`compare-feature-${index}`}>
                        <div
                          className="flex relative text-gray-700 text-caption1 py-[16px]"
                        >
                          <p
                            className={`${feature?.toolTip ? "cursor-pointer border-b border-dotted" : null} text-14/20 tracking-tight lg:group-hover:text-citron-900`}
                            onMouseEnter={() => setActiveToolTip(feature?.columnOne)}
                            onMouseLeave={() => setToolTip('')}
                          >{feature.columnOne}</p>
                          <span
                            className={`${feature?.toolTip ? "relative group hidden lg:block" : null}`}
                          >

                            {feature?.toolTip && (
                              <span className={`absolute ${toolTip === feature.columnOne ? "flex" : "hidden"} absolute ${feature.topMargin ? "top-[-30px]" : "top-[-15px]"} ${feature?.largeTooltip && !feature.topMargin ? "top-[-35px]" : "top-[-15px]"} left-2 py-1 px-[12px] text-white bg-gray-900 ${feature?.largeTooltip ? "w-[480px]" : "w-[280px]"} z-1 rounded-4 text-12/16`}>
                                {feature.toolTip}
                                <div className="absolute left-[-5px] top-[45%] bottom-0 right-0 m-auto">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="5" height="8" viewBox="0 0 5 8" fill="none">
                                    <path d="M0.488043 4.39043C0.23784 4.19027 0.23784 3.80973 0.488043 3.60957L5 2.5828e-07L5 8L0.488043 4.39043Z" fill="#1C1E20" />
                                  </svg>
                                </div>
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-center text-caption1 text-blue-900 py-[16px] w-[160px] md:w-[250px]">{feature.columnTwo}</div>
                        <div className="flex justify-center text-caption1 text-blue-900 py-[16px] w-[160px] md:w-[250px]">{feature.columnThree}</div>
                        <div className="flex justify-center text-caption1 text-blue-900 py-[16px] w-[160px] md:w-[250px]">{feature.columnFour}</div>
                        <div className="h-[1px] bg-gray-200 col-span-full" />
                      </React.Fragment>
                    ))}
                  </motion.div>
                )}
              </div>
            </>
          ))}
        </div>
      </section>
      <div className="hidden md:block h-30 md:h-[10vw] relative w-full overflow-hidden mt-[-120px]">
        <IconTileBreakup data-fill-color="#FFF" className="absolute left-[-100vw] right-[-100vw] bottom-[-1px] w-[300vw] md:left-0 md:right-0 md:w-full h-auto text-gray-100" />
      </div>
      <div className="flex md:hidden relative w-full mt-[-80px]">
        <svg xmlns="http://www.w3.org/2000/svg" width="360" height="35" viewBox="0 0 360 35" fill="none" className="w-full">
          <path fillRule="evenodd" clipRule="evenodd" d="M-222 0.302527H-87.8333L-87.8337 11.605H46.333V0.302444L-87.8333 0.302527V-11H-222V0.302527ZM582 0.302527H447.833V-11H582V0.302527ZM447.833 0.302527L447.833 11.605H313.667V0.302444L447.833 0.302527ZM180.5 22.9079H313.667V11.6054H180.5H179.5H46.3331V22.9079H179.5H180.5ZM582 40.921H447.833V35.0002H314V23.0002H447.833V14.4307H582V40.921ZM-87.8333 40.921H-222V14.4307H-87.8333V23.0002H45.9999V35.0002H-87.8333V40.921Z" fill="white" />
        </svg>
      </div>
    </>
  );
};

export default ComparePlans;
